/**
 * Sidebar module-creation hook. Extracted verbatim (behavior-preserving) from
 * layout/Sidebar.tsx — bundles the new-module wizard state cluster
 * (`isNewModuleMenuOpen`, `newModuleStep`, `newModuleName`, `newModuleNameError`,
 * `newModuleSearch`, `isCreatingModule`), the synchronous double-submit guard
 * ref (`isCreatingModuleRef`), the name-input ref, the focus-on-step effect, and
 * the async `handleCreateModule` callback.
 *
 * Hook-order safety: this cluster of `useState`s + two `useRef`s was already
 * contiguous (state lines) and the focus `useEffect` + `handleCreateModule`
 * `useCallback` followed shortly after with nothing reading them in between.
 * Collapsing them into one hook call at the cluster's lead position keeps a
 * stable render-to-render hook order. The `handleCreateModule` dependency array
 * is byte-identical to the original (`navigate` is retained even though unused,
 * to preserve the exact memo identity contract).
 *
 * The position/Module-shape math is delegated to the `buildNewModule` kernel
 * (a verbatim port pinned by moduleCreation.test.ts) so the create flow stays
 * byte-for-byte equivalent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { useNavigate } from 'react-router-dom';
import { createPluginInstance } from '@/modules/_infrastructure/pluginApi';
import { prefetchModuleChunk } from '@/lib/prefetch-module';
import { queryKeys } from '@/lib/query/keys';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import { PLUGIN_TYPE_MAP } from '../constants';
import { buildNewModule } from '../moduleCreation';

interface UseModuleCreationArgs {
  activeConversation: Conversation | null;
  addModule: (conversationId: string, module: Module) => Promise<void> | void;
  navigate: ReturnType<typeof useNavigate>;
  workspaceId: string;
  queryClient: QueryClient;
}

export function useModuleCreation({
  activeConversation,
  addModule,
  navigate,
  workspaceId,
  queryClient,
}: UseModuleCreationArgs) {
  const [isNewModuleMenuOpen, setIsNewModuleMenuOpen] = useState(false);
  const [newModuleStep, setNewModuleStep] = useState<{ type: string; label: string; emoji: string } | null>(null);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleNameError, setNewModuleNameError] = useState('');
  const [newModuleSearch, setNewModuleSearch] = useState('');
  const [isCreatingModule, setIsCreatingModule] = useState(false);
  const isCreatingModuleRef = useRef(false);
  const newModuleNameInputRef = useRef<HTMLInputElement>(null);

  // Focus name input when entering name step
  useEffect(() => {
    if (newModuleStep && newModuleNameInputRef.current) {
      newModuleNameInputRef.current.focus();
      newModuleNameInputRef.current.select();
    }
  }, [newModuleStep]);

  const handleCreateModule = useCallback(async (type: string, label: string) => {
    if (!activeConversation?.id || isCreatingModuleRef.current) return;
    isCreatingModuleRef.current = true;
    setIsCreatingModule(true);
    // Warm the module's lazy chunk while the create round-trips — the new
    // module renders inline in chat immediately after, with no hover
    // opportunity (no-op for bundled types, deduped per chunk).
    prefetchModuleChunk(type);
    try {
      const newModule = buildNewModule({
        conversationId: activeConversation.id,
        type,
        label,
        existingModules: activeConversation.modules || [],
      });

      await addModule(activeConversation.id, newModule);

      // Auto-create plugin instance for plugin-type modules
      const pluginId = PLUGIN_TYPE_MAP[type];
      if (pluginId) {
        try {
          await createPluginInstance(pluginId, activeConversation.id, {});
        } catch (err) {
          console.warn('[Sidebar] Plugin instance creation failed (non-blocking):', err);
        }
      }

      // Refresh sidebar module list so the new module appears immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });

      // Show the new module inline in the chat
      window.dispatchEvent(new CustomEvent('xavior:log-chat-message', {
        detail: { message: `Show ${label}`, moduleId: newModule.id },
      }));

      // Close modal on success
      setIsNewModuleMenuOpen(false);
      setNewModuleStep(null);
      setNewModuleName('');
      setNewModuleNameError('');
    } catch (error) {
      console.error('[Sidebar] Failed to create module:', error);
      setNewModuleNameError('Failed to create module. Please try again.');
    } finally {
      isCreatingModuleRef.current = false;
      setIsCreatingModule(false);
    }
  }, [activeConversation, addModule, navigate, workspaceId, queryClient]);

  return {
    isNewModuleMenuOpen,
    setIsNewModuleMenuOpen,
    newModuleStep,
    setNewModuleStep,
    newModuleName,
    setNewModuleName,
    newModuleNameError,
    setNewModuleNameError,
    newModuleSearch,
    setNewModuleSearch,
    isCreatingModule,
    newModuleNameInputRef,
    handleCreateModule,
  };
}
